import type { JsonValue } from "../types/data";
import type {
  WidgetConfigCategory,
  WidgetConfigCondition,
  WidgetConfigFragment,
  WidgetConfigOverlay,
  WidgetConfigOverlayFn,
  WidgetConfigProperty,
  WidgetConfigPropertyOverride,
  WidgetConfigSchema,
  WidgetSettings,
} from "../types/settings";
import { WIDGET_CONFIG_CATEGORIES, WIDGET_CONFIG_GROUPS } from "../types/settings";

/** Keeps the literal keys of a fragment's properties. */
export function defineConfigFragment<const P extends Record<string, WidgetConfigProperty>>(
  $id: string,
  version: number,
  properties: P,
): WidgetConfigFragment & { readonly properties: P } {
  return { $id, version, properties };
}

/**
 * The frame every widget has, whatever it shows: its title and how viewers may
 * handle it (open it full size, fold it). The host always applies it; a widget
 * schema may restate one of its properties (a note has no default title).
 */
export const WIDGET_FRAME_CONFIG = defineConfigFragment("nexus:widget-frame", 1, {
  title: {
    type: "string",
    title: "Title",
    "x-ui": { category: "texts", control: "template", order: 0, keepDefault: true },
  },
  fullscreenable: {
    type: "boolean",
    title: "Full-size view",
    description: "Viewers can open it full size",
    // No default of its own: the dashboard's former focus mode decides (the host passes it).
    "x-ui": {
      category: "display",
      group: "viewer",
      order: 90,
      summary: "Full-size view",
      keepDefault: true,
    },
  },
  collapsible: {
    type: "boolean",
    title: "Collapsible",
    description: "Viewers can fold it to its title",
    default: false,
    "x-ui": {
      category: "display",
      group: "viewer",
      order: 91,
      summary: "Collapsible",
      resets: ["defaultCollapsed"],
    },
  },
  defaultCollapsed: {
    type: "boolean",
    title: "Start collapsed",
    description: "Folded when the dashboard opens",
    default: false,
    "x-ui": { category: "display", group: "viewer", order: 92, visibleIf: { collapsible: true } },
  },
});

function mergeProperty(
  inherited: WidgetConfigProperty | undefined,
  own: WidgetConfigProperty | WidgetConfigPropertyOverride,
  key: string,
): WidgetConfigProperty {
  if (!inherited) {
    if (!own.type || !own.title || !own["x-ui"]?.category)
      throw new Error(`Setting "${key}" needs a type, a title and an x-ui category`);
    return own as WidgetConfigProperty;
  }
  return {
    ...inherited,
    ...own,
    "x-ui": { ...inherited["x-ui"], ...own["x-ui"] },
  } as WidgetConfigProperty;
}

/**
 * A widget's settings: the frame, the fragments it extends in order, then its own
 * properties, each one adding a setting or restating part of an inherited one.
 *
 * ```ts
 * settings: defineWidgetSettings({
 *   id: "blocking-overview",
 *   extends: [ANIMATE_VALUE_CONFIG],
 *   properties: {
 *     detail: { type: "string", title: "Detail", default: "isps", oneOf: DETAILS,
 *               "x-ui": { category: "display", summary: true } },
 *   },
 * }),
 * ```
 */
export function defineWidgetSettings({
  id,
  version = 1,
  extends: fragments = [],
  properties = {},
  overlay,
}: {
  /** The widget id. */
  id: string;
  /** This schema's own version; raise it when a setting changes meaning. */
  version?: number;
  extends?: readonly WidgetConfigFragment[];
  properties?: Readonly<Record<string, WidgetConfigProperty | WidgetConfigPropertyOverride>>;
  overlay?: WidgetConfigOverlayFn;
}): WidgetSettings {
  const merged: Record<string, WidgetConfigProperty> = {};
  for (const fragment of [WIDGET_FRAME_CONFIG, ...fragments]) {
    for (const [key, property] of Object.entries(fragment.properties))
      merged[key] = mergeProperty(merged[key], property, key);
  }
  for (const [key, property] of Object.entries(properties))
    merged[key] = mergeProperty(merged[key], property, key);
  const schema: WidgetConfigSchema = {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: `nexus:widget-config/${id}`,
    version,
    type: "object",
    properties: merged,
    additionalProperties: true,
    "x-extends": [WIDGET_FRAME_CONFIG, ...fragments].map(
      (fragment) => `${fragment.$id}@${fragment.version}`,
    ),
  };
  return overlay ? { schema, overlay } : { schema };
}

/** The settings of a widget that declares none: its frame. */
export function widgetSettingsOf(
  definition: { id: string; settings?: WidgetSettings } | undefined,
): WidgetSettings {
  return definition?.settings ?? defineWidgetSettings({ id: definition?.id ?? "unknown" });
}

/** The defaults the schema declares: the configuration a new instance reads. */
export function configDefaults(schema: WidgetConfigSchema): Record<string, JsonValue> {
  return Object.fromEntries(
    Object.entries(schema.properties)
      .filter(([, property]) => property.default !== undefined)
      .map(([key, property]) => [key, property.default as JsonValue]),
  );
}

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** A setting's value as the widget reads it: stored, else the overlay's or the schema's default. */
export function configValue(
  schema: WidgetConfigSchema,
  config: Readonly<Record<string, JsonValue | undefined>>,
  key: string,
  overlay?: WidgetConfigOverlay,
  contextDefaults?: Readonly<Record<string, JsonValue>>,
): JsonValue | undefined {
  const stored = config[key];
  if (stored !== undefined && stored !== null) return stored;
  return overlay?.fallbacks?.[key] ?? contextDefaults?.[key] ?? schema.properties[key]?.default;
}

/**
 * Whether a condition holds. `values` reads a setting's effective value; keys
 * starting with `$` read host-owned values.
 */
export function matchesCondition(
  condition: WidgetConfigCondition | undefined,
  values: (key: string) => unknown,
): boolean {
  if (!condition) return true;
  return Object.entries(condition).every(([key, expected]) => {
    const value = values(key);
    if (expected !== null && typeof expected === "object") {
      if ("not" in expected) return value !== expected.not;
      if ("in" in expected) return expected.in.includes(value as never);
      return expected.set ? !isEmpty(value) : isEmpty(value);
    }
    return value === expected;
  });
}

/**
 * The configuration changes for setting `key` to `value`: the value itself
 * (cleared when it is the default, unless the setting keeps its default) and
 * the settings it resets. `null` clears a key.
 */
export function configChanges(
  schema: WidgetConfigSchema,
  key: string,
  value: JsonValue,
): [string, JsonValue][] {
  const property = schema.properties[key];
  const isDefault =
    property?.default !== undefined &&
    !property["x-ui"].keepDefault &&
    JSON.stringify(value) === JSON.stringify(property.default);
  return [
    [key, isDefault || value === "" ? null : value],
    ...(property?.["x-ui"].resets ?? []).map((reset): [string, JsonValue] => [reset, null]),
  ];
}

const DEFAULT_ORDER = 50;

/**
 * The settings of a category in display order: by `order`, then the display
 * groups' order, then declaration order; advanced settings last.
 */
export function categoryProperties(
  schema: WidgetConfigSchema,
  category: WidgetConfigCategory,
): [string, WidgetConfigProperty][] {
  const entries = Object.entries(schema.properties).filter(
    ([, property]) => property["x-ui"].category === category,
  );
  const rank = ([, property]: [string, WidgetConfigProperty]) => [
    property["x-ui"].advanced ? 1 : 0,
    property["x-ui"].order ?? DEFAULT_ORDER,
  ];
  return entries
    .map((entry, index) => ({ entry, index, rank: rank(entry) }))
    .sort((a, b) => a.rank[0] - b.rank[0] || a.rank[1] - b.rank[1] || a.index - b.index)
    .map(({ entry }) => entry);
}

/**
 * What is wrong with a schema, for tests: unknown categories or groups, a default
 * outside its options, a condition reading an unknown setting.
 */
export function invalidSettings(schema: WidgetConfigSchema): string[] {
  return Object.entries(schema.properties).flatMap(([key, property]) => {
    const ui = property["x-ui"];
    const problems: string[] = [];
    if (!WIDGET_CONFIG_CATEGORIES.includes(ui.category))
      problems.push(`${key}: unknown category "${ui.category}"`);
    if (ui.group && !WIDGET_CONFIG_GROUPS.includes(ui.group))
      problems.push(`${key}: unknown group "${ui.group}"`);
    if (property.oneOf && property.default !== undefined && property.default !== null) {
      if (!property.oneOf.some((option) => option.const === property.default))
        problems.push(`${key}: default is not one of its options`);
    }
    for (const condition of Object.keys(ui.visibleIf ?? {}))
      if (!condition.startsWith("$") && !(condition in schema.properties))
        problems.push(`${key}: visibleIf reads unknown setting "${condition}"`);
    return problems;
  });
}

function matchesType(value: JsonValue, type: WidgetConfigProperty["type"]): boolean {
  const types: readonly string[] = Array.isArray(type) ? type : [type as string];
  return types.some((expected) =>
    expected === "integer"
      ? Number.isInteger(value)
      : expected === "number"
        ? typeof value === "number" && Number.isFinite(value)
        : typeof value === expected,
  );
}

/**
 * The stored settings that do not match the widget's schema: a wrong type, a
 * value outside its choices or its bounds. Keys the schema does not declare are
 * the host's or the widget's own (labels, height…) and are not checked. The
 * schema is the only description of a widget's settings: neither the host nor
 * the backend keeps another.
 */
export function settingsIssues(
  schema: WidgetConfigSchema,
  config: Readonly<Record<string, JsonValue | undefined>>,
): string[] {
  return Object.entries(schema.properties).flatMap(([key, property]) => {
    const value = config[key];
    if (value === undefined || value === null) return [];
    if (!matchesType(value, property.type)) return [`${key}: expected ${String(property.type)}`];
    if (property.oneOf && !property.oneOf.some((option) => option.const === value))
      return [`${key}: not one of its choices`];
    if (typeof value === "number") {
      if (property.minimum !== undefined && value < property.minimum)
        return [`${key}: below ${property.minimum}`];
      if (property.maximum !== undefined && value > property.maximum)
        return [`${key}: above ${property.maximum}`];
    }
    return [];
  });
}

/**
 * The configuration a widget renders with: stored values that do not match its
 * schema are dropped, so the widget reads its default instead. A configuration
 * written by hand, imported or saved by an older version never reaches a widget
 * in a shape its schema does not allow.
 */
export function conformingConfig<Config extends Readonly<Record<string, JsonValue | undefined>>>(
  schema: WidgetConfigSchema,
  config: Config,
): Config {
  const issues = settingsIssues(schema, config);
  if (issues.length === 0) return config;
  const invalid = new Set(issues.map((issue) => issue.slice(0, issue.indexOf(":"))));
  return Object.fromEntries(
    Object.entries(config).filter(([key]) => !invalid.has(key)),
  ) as Config;
}
